import React, { useState, useEffect, useRef, useCallback } from "react";
import axiosInstance from "../../../axiosConfig";
import Select from "react-select";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingScreen from "../../../Components/LoadingScreen/LoadingScreen";

function Discounts() {
  const [formData, setFormData] = useState({
    discountPlanId: 0,
    monthPlanType: "",
    percentage: 0,
    cuponCode: "",
    moduleIds: [],
  });

  const [modules, setModules] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [viewMode, setViewMode] = useState("product"); // NEW
  const [selectedProduct, setSelectedProduct] = useState(null); // NEW

  const formRef = useRef(null);

  const monthPlanMapping = {
    "3 Months": "THREE_MONTHS",
    "6 Months": "SIX_MONTHS",
    "12 Months": "TWELVE_MONTHS",
  };

  // -------------------------
  // Load Product from Local Storage
  // -------------------------
  useEffect(() => {
    const sp = localStorage.getItem("selectedProduct");
    if (sp) {
      try {
        setSelectedProduct(JSON.parse(sp));
      } catch (e) {}
    }
  }, []);

  // -------------------------
  // Fetch Modules (always all)
  // -------------------------
  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("/modules");
        setModules(response.data?.data || []);
      } catch (error) {
        toast.error("Failed to load modules.");
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, []);

  // -------------------------
  // Fetch Coupons based on View Mode
  // -------------------------
  const fetchCoupons = useCallback(async () => {
    if (!selectedProduct) return;

    try {
      const endpoint =
        viewMode === "product"
          ? `/discount-plans/${selectedProduct.productId}/products`
          : "/discount-plans/all";

      const response = await axiosInstance.get(endpoint);

      console.log("Fetched Coupons:", response.data);

      const raw = response.data?.data;

      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];

      setCoupons(list);
    } catch (error) {
      toast.error("Failed to fetch discount plans.");
    }
  }, [viewMode, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) fetchCoupons();
  }, [fetchCoupons, selectedProduct]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  // -------------------------
  // Coupon Code Generator
  // -------------------------
  const generateRandomCoupon = () => {
    const randomString = Math.random()
      .toString(36)
      .substring(2, 10)
      .toUpperCase();
    setFormData((prev) => ({ ...prev, cuponCode: randomString }));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formData.cuponCode);
    toast.success("Coupon copied!");
  };

  // -------------------------
  // Form Changes
  // -------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "monthPlanType") {
      const mapped = monthPlanMapping[value] || value;
      setFormData((prev) => ({ ...prev, monthPlanType: mapped }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleModuleSelect = (opts) => {
    const ids = opts.map((o) => o.value);
    setFormData((prev) => ({ ...prev, moduleIds: ids }));
  };

  // -------------------------
  // Validation
  // -------------------------
  const validateForm = () => {
    const errs = {};
    let valid = true;

    if (!formData.monthPlanType) {
      errs.monthPlanType = "Month Plan Type is required";
      valid = false;
    }

    if (formData.percentage <= 0 || formData.percentage > 100) {
      errs.percentage = "Discount must be between 1–100%";
      valid = false;
    }

    if (formData.moduleIds.length === 0) {
      errs.moduleIds = "Select at least one module";
      valid = false;
    }

    setErrors(errs);
    return valid;
  };

  // -------------------------
  // Submit
  // -------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const payload = isEdit
      ? { ...formData, discountId: formData.discountPlanId }
      : formData;

    const endpoint = isEdit
      ? `/discount-plans/update/${formData.discountPlanId}`
      : "/discount-plans/create";

    const method = isEdit ? "put" : "post";

    try {
      const res = await axiosInstance[method](endpoint, payload);

      if (res.status === 200) {
        toast.success(isEdit ? "Updated!" : "Created!");

        fetchCoupons();

        setFormData({
          discountPlanId: 0,
          monthPlanType: "",
          percentage: 0,
          cuponCode: "",
          moduleIds: [],
        });

        setIsEdit(false);
        setErrors({});
      }
    } catch (error) {
      toast.error("Failed to save discount plan.");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------
  // Delete
  // -------------------------
  const handleDelete = async (id) => {
    try {
      const res = await axiosInstance.delete(`/discount-plans/delete/${id}`);

      if (res.status === 200) {
        toast.success("Deleted!");
        fetchCoupons();
      }
    } catch (error) {
      toast.error("Failed to delete.");
    }
  };

  // -------------------------
  // Edit
  // -------------------------
  const handleEdit = (coupon) => {
    setFormData({
      discountPlanId: coupon.discountPlanId,
      monthPlanType: coupon.monthPlanType,
      percentage: coupon.percentage,
      cuponCode: coupon.cuponCode,
      moduleIds: coupon.modules.map((m) => m.moduleId),
    });

    setIsEdit(true);

    if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handleCancel = () => {
    setIsEdit(false);
    setFormData({
      discountPlanId: 0,
      monthPlanType: "",
      percentage: 0,
      cuponCode: "",
      moduleIds: [],
    });
    setErrors({});
  };

  const moduleOptions = modules.map((m) => ({
    value: m.moduleId,
    label: m.moduleName,
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="max-w-7xl mx-auto">
        {/* ========================== */}
        {/* HEADER + TOGGLE BUTTONS   */}
        {/* ========================== */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">
              Discount Management
            </h2>
            <p className="text-gray-600">
              Configure discount plans per product
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Product Badge */}
            {selectedProduct && (
              <div className="bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg">
                <p className="text-sm text-gray-500">Product</p>
                <p className="font-semibold text-gray-900">
                  {selectedProduct.productName}
                </p>
              </div>
            )}

            {/* View Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleViewModeChange("product")}
                className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                  viewMode === "product"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Product View
              </button>

              <button
                onClick={() => handleViewModeChange("all")}
                className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                  viewMode === "all"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                All Discounts
              </button>
            </div>
          </div>
        </div>

        {/* ========================================================== */}
        {/* FORM SECTION — EXACTLY YOUR DESIGN (UNTOUCHED)             */}
        {/* ========================================================== */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          <div className="xl:col-span-2">
            <div
              ref={formRef}
              className="bg-white rounded-xl shadow-xl border p-6 space-y-6"
            >
              <h2 className="text-xl font-bold">
                {isEdit ? "Edit Discount Plan" : "Create Discount Plan"}
              </h2>

              {/* Month Plan */}
              <div className="space-y-1">
                <label className="text-sm font-semibold">
                  Subscription Plan
                </label>
                <select
                  name="monthPlanType"
                  value={
                    Object.keys(monthPlanMapping).find(
                      (key) => monthPlanMapping[key] === formData.monthPlanType
                    ) || ""
                  }
                  onChange={handleChange}
                  className="w-full border p-3 rounded-lg"
                >
                  <option value="">Choose duration…</option>
                  <option value="3 Months">3 Months</option>
                  <option value="6 Months">6 Months</option>
                  <option value="12 Months">12 Months</option>
                </select>

                {errors.monthPlanType && (
                  <p className="text-red-600 text-sm">{errors.monthPlanType}</p>
                )}
              </div>

              {/* Percentage */}
              <div className="space-y-1">
                <label className="text-sm font-semibold">Discount %</label>
                <input
                  type="number"
                  name="percentage"
                  min="1"
                  max="100"
                  value={formData.percentage}
                  onChange={handleChange}
                  className="w-full border p-3 rounded-lg"
                />

                {errors.percentage && (
                  <p className="text-red-600 text-sm">{errors.percentage}</p>
                )}
              </div>

              {/* Coupon Code */}
              <div className="space-y-1">
                <label className="text-sm font-semibold">Coupon Code</label>

                <div className="flex gap-2">
                  <input
                    type="text"
                    name="cuponCode"
                    value={formData.cuponCode}
                    onChange={handleChange}
                    className="flex-1 border p-3 rounded-lg"
                  />

                  <button
                    type="button"
                    onClick={generateRandomCoupon}
                    className="px-3 bg-purple-600 text-white rounded-lg"
                  >
                    Generate
                  </button>

                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="px-3 bg-gray-500 text-white rounded-lg"
                  >
                    Copy
                  </button>
                </div>
              </div>

              {/* Modules */}
              <div className="space-y-1">
                <label className="text-sm font-semibold">
                  Applicable Modules
                </label>

                {loading ? (
                  <LoadingScreen />
                ) : (
                  <Select
                    isMulti
                    options={moduleOptions}
                    value={moduleOptions.filter((o) =>
                      formData.moduleIds.includes(o.value)
                    )}
                    onChange={handleModuleSelect}
                    placeholder="Select modules..."
                  />
                )}

                {errors.moduleIds && (
                  <p className="text-red-600 text-sm">{errors.moduleIds}</p>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg"
              >
                {isEdit ? "Update Plan" : "Create Plan"}
              </button>

              {isEdit && (
                <button
                  onClick={handleCancel}
                  className="w-full bg-gray-300 text-black mt-2 py-3 rounded-lg"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </div>

          {/* ========================================================== */}
          {/* LIST SECTION (UNTOUCHED, JUST DATA CHANGED FROM API)      */}
          {/* ========================================================== */}
          <div className="xl:col-span-3">
            <div className="bg-white rounded-xl p-6 shadow-xl border">
              <h2 className="text-xl font-bold mb-4">Active Discount Plans</h2>

              {coupons.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No discount plans found.
                </div>
              ) : (
                <div className="grid gap-4">
                  {coupons.map((c) => (
                    <div
                      key={c.discountPlanId}
                      className="bg-gray-50 p-5 rounded-xl border shadow-sm"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-bold text-lg">{c.cuponCode}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {c.monthPlanType?.replace(/_/g, " ")}
                          </div>

                          <div className="mt-3">
                            <p className="font-medium">Modules:</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {c.modules.map((m) => (
                                <span
                                  key={m.moduleId}
                                  className="bg-green-100 text-green-800 px-2 py-1 rounded-lg text-xs"
                                >
                                  {m.moduleName}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(c.cuponCode);
                              toast.success("Copied!");
                            }}
                            className="p-2 bg-blue-500 text-white rounded-lg"
                          >
                            Copy
                          </button>

                          <button
                            onClick={() => handleEdit(c)}
                            className="p-2 bg-yellow-500 text-white rounded-lg"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(c.discountPlanId)}
                            className="p-2 bg-red-600 text-white rounded-lg"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 font-bold text-red-600">
                        {c.percentage}% OFF
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Discounts;
